<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { type TemplateDocument } from "@/Modules/TemplateDocuments/Models/Domain";

import { TcModal, TcInput } from "@/Components/UI";
import DocumentCard from "@/Components/Extend/Documents/DocumentCard.vue";

import { isValidString } from "@/Utils/String";

export interface TemplateSelectModalProps {
  documents: TemplateDocument[];
}

const { t } = useI18n();

const props = defineProps<TemplateSelectModalProps>();

const searchText = ref<string>("");

const emit = defineEmits<{
  (event: "select", template: TemplateDocument): void;
  (event: "close"): void;
}>();

const filteredDocuments = computed(() => {
  return props.documents.filter((doc) => {
    if (!isValidString(searchText.value)) return true;

    return doc.currentVersion.title.toLowerCase().includes(searchText.value.toLowerCase());
  });
});

const handleSelectTemplate = (template: TemplateDocument) => {
  emit("select", template);
};
</script>

<template>
  <TcModal :title="t('documents.templates.select_template.title')" @clickOutside="emit('close')" canClose>
    <template #description>
      <TcInput
        v-model="searchText"
        name="template_search"
        :placeholder="t('documents.templates.select_template.search.label')"
        startIcon="Search"
      />

      <div v-if="filteredDocuments.length === 0" class="typography-display-low-s text-center">
        {{ t("documents.templates.select_template.empty_state.title") }}
      </div>

      <div class="documents_list">
        <DocumentCard
          v-for="document in filteredDocuments"
          class="documents_list__card"
          canSelect
          :key="document.uuid"
          :document="document"
          @select="handleSelectTemplate"
        />
      </div>
    </template>
  </TcModal>
</template>

<style scoped lang="scss">
.documents_list {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: $space-2;
}
</style>
